/**
 * @fileoverview CardContent component
 * Displays the content section of a deal card including vehicle information, deal metadata, comments, and last activity
 * Part of the RecentDealCard dumb component hierarchy
 */

import { ChatBubbleLeftIcon } from '@interstate/components/Icons/ChatBubbleLeftIcon';
import { ClockIcon } from '@interstate/components/Icons/ClockIcon';
import CardMetaItem from '../CardMetaItem/CardMetaItem';
import { Comment, DealDetails, LastUpdated, StyledCardContent, VehicleInfo } from './CardContent.style';
import type { RecentDealListItem } from '../../types/RecentDeals';

/**
 * CardContent component
 * Renders the body of a recent deal card with:
 * - Vehicle name and stock number
 * - Deal metadata (deal type, trade-in, sales rep, deal status)
 * - Optional comment with icon
 * - Optional last activity summary with timestamp icon
 *
 * @param deal - The recent deal object containing all display data
 * @returns Styled card content section with vehicle info, metadata, and activity details
 */
const CardContent: React.FC<{ deal: RecentDealListItem }> = ({ deal }) => (
    <StyledCardContent data-testid="card-content">
        <VehicleInfo data-testid="vehicle-info">
            {deal.vehicle.name}
            {deal.vehicle.stockNumber && (
                <>
                    <span className="pipe">|</span>
                    <span data-testid="stock-number">Stock# {deal.vehicle.stockNumber}</span>
                </>
            )}
        </VehicleInfo>
        <DealDetails data-testid="deal-details">
            <CardMetaItem label="Deal Type" value={deal.dealType} data-testid="deal-type" />
            <CardMetaItem label="Trade In" value={deal.tradeInLabel} data-testid="trade-in" />
            <CardMetaItem label="Sales Rep" value={deal.primarySalesperson} data-testid="sales-rep" />
            <CardMetaItem label="Deal Status" value={deal.dealStatus} data-testid="deal-status" />
        </DealDetails>
        {deal.comment && (
            <Comment data-testid="deal-comment">
                <ChatBubbleLeftIcon />{' '}
                <span className="two-line-ellipsis" title={deal.comment}>
                    {deal.comment}
                </span>
            </Comment>
        )}
        {deal.lastActivitySummary && (
            <LastUpdated data-testid="last-activity">
                <ClockIcon /> {deal.lastActivitySummary}
            </LastUpdated>
        )}
    </StyledCardContent>
);

export default CardContent;
